import {
  collection, getDocs, addDoc, setDoc, doc,
  deleteDoc, query, where, Timestamp,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, storage, secondaryAuth } from '../firebase/firebase';
import {
  RESTAURANT_COLLECTION, flattenRestaurantDocs, toNestedRestaurant,
} from '../utills/restaurantAdapter';

const COL = RESTAURANT_COLLECTION;
const restaurantCollection = collection(db, COL);

// ── Storage helpers ──────────────────────────────────────────────

export async function uploadRestaurantImage(file: File, docId: string, slot: string): Promise<string> {
  const path = `restaurant/${docId}_${slot}_${file.name}`;
  const imgRef = storageRef(storage, path);
  await uploadBytes(imgRef, file);
  return getDownloadURL(imgRef);
}

/** Silently delete a Firebase Storage file by its download URL */
export async function deleteStorageUrl(url: string): Promise<void> {
  if (!url || !url.includes('firebasestorage')) return;
  try {
    const match = url.match(/\/o\/(.+?)\?/);
    if (!match) return;
    const path = decodeURIComponent(match[1]);
    await deleteObject(storageRef(storage, path));
  } catch (e) {
    console.warn('Storage delete skipped:', e);
  }
}

// ── CRUD ─────────────────────────────────────────────────────────

/** Register restaurant with Firebase Auth (secondary app) + Firestore */
export async function registerRestaurant(email: string, password: string, data: any): Promise<string> {
  const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
  const uid = credential.user.uid;
  await signOut(secondaryAuth);

  // Write the new nested schema to `zapee_restaurant_partner`.
  await setDoc(doc(db, COL, uid), {
    ...toNestedRestaurant({ ...data, uid, id: uid, storeuid: uid, email }, true),
    storeuid: uid,
    uid,
    id: uid,
    email,
    createdAt: Timestamp.now(),
  });
  return uid;
}

export async function createRestaurant(data: any): Promise<string> {
  const docRef = await addDoc(restaurantCollection, {
    ...toNestedRestaurant(data, true),
    createdAt: Timestamp.now(),
  });
  // back-fill id / uid / storeuid with the generated doc ID
  await setDoc(docRef, { id: docRef.id, uid: docRef.id, storeuid: docRef.id }, { merge: true });
  return docRef.id;
}

export async function getRestaurants(): Promise<any[]> {
  const snap = await getDocs(restaurantCollection);
  return flattenRestaurantDocs(snap.docs);
}

export async function getRestaurantsByBranch(branchId: string): Promise<any[]> {
  if (!branchId) return getRestaurants();
  const q = query(restaurantCollection, where('branchId', '==', branchId));
  const snap = await getDocs(q);
  return flattenRestaurantDocs(snap.docs);
}

export async function updateRestaurant(docId: string, data: any): Promise<void> {
  // Deep-merge the nested payload so untouched sub-fields are preserved.
  await setDoc(doc(db, COL, docId), toNestedRestaurant(data), { merge: true });
}

/** Delete restaurant doc + its image + all banner images from Storage */
export async function deleteRestaurant(docId: string, imageUrl?: string, bannerImages?: string[]): Promise<void> {
  await deleteDoc(doc(db, COL, docId));
  if (imageUrl) await deleteStorageUrl(imageUrl);
  for (const url of bannerImages ?? []) {
    await deleteStorageUrl(url);
  }
}

// legacy aliases used by other screens
export { getRestaurants as getOrders };
export { createRestaurant as createOrder };
export { updateRestaurant as updateOrder };
